import { useCallback, useRef } from 'react';

export function useVideoSync({ onPause, onPlay, onSeek } = {}) {
  const playerRef      = useRef(null);
  const channelRef     = useRef(null);
  const isDualRef      = useRef(false);
  const currentTimeRef = useRef(0); // Traccia il tempo ricevuto via BroadcastChannel

  // Registra il riferimento al player locale
  const registerPlayer = useCallback((player) => {
    playerRef.current = player;
  }, []);

  // Inizializza BroadcastChannel per dual screen
  const initDualChannel = useCallback(() => {
    if (!channelRef.current) {
      channelRef.current = new BroadcastChannel('tkd-sync');
      channelRef.current.onmessage = (e) => {
        const { type, time } = e.data;
        if (type === 'play'        && onPlay)  onPlay();
        if (type === 'pause'       && onPause) onPause();
        if (type === 'seek'        && onSeek)  onSeek(time);
        if (type === 'timeupdate'  && time != null) currentTimeRef.current = time;
      };
    }
    isDualRef.current = true;
  }, [onPause, onPlay, onSeek]);

  const closeDualChannel = useCallback(() => {
    if (channelRef.current) {
      channelRef.current.close();
      channelRef.current = null;
    }
    isDualRef.current = false;
  }, []);

  // Comandi verso il player locale e/o la finestra video (via BC)
  const sendPlay = useCallback(() => {
    try { if (playerRef.current?.playVideo) playerRef.current.playVideo(); } catch {}
    try { if (playerRef.current?.play)      playerRef.current.play(); }      catch {}
    if (channelRef.current) channelRef.current.postMessage({ type: 'play' });
  }, []);

  const sendPause = useCallback(() => {
    try { if (playerRef.current?.pauseVideo) playerRef.current.pauseVideo(); } catch {}
    try { if (playerRef.current?.pause)      playerRef.current.pause(); }      catch {}
    if (channelRef.current) channelRef.current.postMessage({ type: 'pause' });
  }, []);

  const sendSeek = useCallback((time) => {
    try { if (playerRef.current?.seekTo) playerRef.current.seekTo(time, true); }       catch {}
    try { if (playerRef.current?.currentTime != null) playerRef.current.currentTime = time; } catch {}
    if (channelRef.current) channelRef.current.postMessage({ type: 'seek', time });
  }, []);

  const sendSpeed = useCallback((rate) => {
    if (playerRef.current?.setPlaybackRate)     playerRef.current.setPlaybackRate(rate);
    if (playerRef.current?.playbackRate != null) playerRef.current.playbackRate = rate;
    if (channelRef.current) channelRef.current.postMessage({ type: 'speed', rate });
  }, []);

  // Restituisce il tempo corrente: dal player locale o dall'ultimo timeupdate via BC
  const getCurrentTime = useCallback(() => {
    if (playerRef.current?.getCurrentTime) return playerRef.current.getCurrentTime();
    if (playerRef.current?.currentTime != null) return playerRef.current.currentTime;
    return currentTimeRef.current; // Usato in modalità dual (dalla finestra video via BC)
  }, []);

  return {
    registerPlayer,
    initDualChannel,
    closeDualChannel,
    sendPlay,
    sendPause,
    sendSeek,
    sendSpeed,
    getCurrentTime,
  };
}

export default useVideoSync;
